import type { CSSProperties } from "react";

// Astria UI Master Spec v1.0.0: design_tokens, used by the Life Areas card only.
// Nothing here is global: no CSS file, no Tailwind config, no font loading.
// The spec only defines dark surfaces, so the LIGHT theme below keeps the spec's
// brand, area and semantic hues and derives light surfaces / text from them.
export const T = {
  color: {
    primary: "#6C63FF",
    primaryButton: "#685FF5", // primary, 4% darker: white text reaches 4.6:1
    secondary: "#A78BFA",
    accent: "#F59E0B",
    accentSoft: "#FDE68A",
    bg: {
      base: "#F6F6FB",
      surface: "#FFFFFF",
      card: "#F7F7FD",
      overlay: "#E6E5F5",
      modal: "#FFFFFF",
    },
    backdrop: "rgba(27,27,58,0.5)",
    toggleOff: "#CFCDEA",
    text: {
      primary: "#1B1B3A",
      secondary: "#5C5B85",
      muted: "#8D8CB0", // hints and disabled only (3.2:1)
      inverse: "#FFFFFF",
      link: "#5B54E8",
    },
    semantic: {
      success: "#10B981",
      warning: "#F59E0B",
      error: "#EF4444",
      info: "#3B82F6",
      neutral: "#6B7280",
    },
    rings: {
      core: "#6C63FF",
      inner: "#8B83FF",
      middle: "#A78BFA",
      outer: "#C4B5FD",
    },
  },
  radius: { sm: 4, md: 8, lg: 12, xl: 16, xxl: 24, full: 9999 },
  shadow: {
    sm: "0 1px 3px rgba(27,27,58,0.08)",
    md: "0 4px 12px rgba(27,27,58,0.10)",
    lg: "0 8px 24px rgba(27,27,58,0.14)",
    card: "0 18px 50px -28px rgba(27,27,58,0.35)",
    glowPrimary: "0 0 16px rgba(108,99,255,0.35)",
  },
  ms: { instant: 80, fast: 150, normal: 250, slow: 400, xslow: 700 },
  ease: {
    default: "cubic-bezier(0.4, 0, 0.2, 1)",
    spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  font: { family: "Inter, system-ui, sans-serif", accent: "Sora, system-ui, sans-serif" },
} as const;

// typography.scale: [size px, line-height px]
const SCALE = {
  xs: [11, 16],
  sm: [13, 20],
  base: [15, 24],
  md: [17, 28],
  lg: [20, 32],
  xl: [24, 36],
  xxl: [30, 40],
  xxxl: [36, 48],
} as const;
export const fs = (k: keyof typeof SCALE, weight = 400): CSSProperties => ({
  fontSize: SCALE[k][0],
  lineHeight: `${SCALE[k][1]}px`,
  fontWeight: weight,
});

// "#RRGGBB" + opacity (0-1) -> "#RRGGBBAA"
export const withAlpha = (hex: string, opacity: number) =>
  `${hex}${Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, "0")}`;

// The backend names a tone ("good", "warning"...); the colour is a design token.
// Calm, low-saturation hues: nothing here should feel like a fitness tracker.
export const TONE_COLOR: Record<string, string> = {
  veryLow: "#D98B7E",
  low: "#D9A55F",
  fair: "#9AA5D8",
  good: "#7DBFA6",
  strong: "#9088D6",
  warning: "#D9A55F",
  success: "#7DBFA6",
  info: "#9AA5D8",
  secondary: T.color.secondary,
};
// Soft up / down colours for the trend arrows and the change pill.
export const SOFT = { up: "#5FB097", down: "#D68C7F" } as const;
// Ring accent colours, innermost first (the spec's ring tokens).
export const ringColor = (index: number) =>
  [T.color.rings.inner, T.color.rings.middle, T.color.rings.outer][Math.min(index, 2)];

// ── readable ink for coloured text on the light surfaces ─────────────────────
// The spec's semantic / area colours are fine as fills, borders and icons, but
// as small text on white they are only ~2-3:1. ink() returns a darker shade of
// the same hue (>= 4.5:1 on white and on the card tint).
const INK: Record<string, string> = {
  "#10B981": "#047857", // success
  "#F59E0B": "#B45309", // warning / accent
  "#EF4444": "#B91C1C", // error
  "#3B82F6": "#1D4ED8", // info
  "#FDE68A": "#A16207", // accent soft
  "#A78BFA": "#6D28D9", // secondary
  "#6C63FF": "#4F46E5", // primary
  "#6B7280": "#4B5563", // neutral
};
const channel = (c: number) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const luminance = (hex: string) => {
  const [r, g, b] = rgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};
const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};
const memo = new Map<string, string>();
export const ink = (color: string): string => {
  const key = color.toUpperCase();
  const hit = memo.get(key) ?? INK[key];
  if (hit) return hit;
  // unknown colour: blend toward the primary text colour until it reads on the card tint
  const [r, g, b] = rgb(key);
  const [dr, dg, db] = rgb(T.color.text.primary);
  let out = key;
  for (let t = 0; t <= 1.0001; t += 0.05) {
    const m = (a: number, d: number) => Math.round(a + (d - a) * t);
    out = `#${[m(r, dr), m(g, dg), m(b, db)].map((v) => v.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
    if (contrast(out, T.color.bg.card) >= 4.6) break;
  }
  memo.set(key, out);
  return out;
};
